import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import Chip from '@mui/material/Chip';
import Spinner from '../Spinner';
import Button from '../Button';
import Toast from '../Toast';
import StatusBadge from '../StatusBadge';
import TenantService, { TenantHealth } from '../../services/tenant.service';

interface TenantHealthCardProps {
  tenantId: number | string;
  onConfigIssuesClick?: () => void;
  compact?: boolean;
}

const TenantHealthCard: React.FC<TenantHealthCardProps> = ({ tenantId, onConfigIssuesClick, compact }) => {
  const { t } = useTranslation();
  const [health, setHealth] = useState<TenantHealth | null>(null);
  const [loading, setLoading] = useState(true);
  const [retrying, setRetrying] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    TenantService.getHealth(tenantId)
      .then((response: any) => setHealth(response?.data ?? null))
      .finally(() => setLoading(false));
  }, [tenantId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRetry = useCallback(async () => {
    setRetrying(true);
    try {
      await TenantService.retryProvisioning(tenantId);
      toast.success(<Toast message={t('tenants.detail.pageTexts.provisioning.retrySuccess')} />, { icon: false });
      load();
    } finally {
      setRetrying(false);
    }
  }, [tenantId, load, t]);

  if (loading || !health) {
    return (
      <div className={compact ? 'py-2 text-center' : 'card mb-4'}>
        {!compact && <hr className="m-0" />}
        <div className="text-center py-3">
          <Spinner />
        </div>
      </div>
    );
  }

  const subscriptionText = (() => {
    if (!health.subscription) return t('tenants.detail.pageTexts.health.noSubscription');
    if (health.subscription.cancelled) return t('tenants.detail.pageTexts.health.cancelled');
    const days = health.subscription.daysUntilExpiry ?? 0;
    return days >= 0
      ? t('tenants.detail.pageTexts.health.expiresIn', { count: days })
      : t('tenants.detail.pageTexts.health.expiredAgo', { count: Math.abs(days) });
  })();
  const subscriptionExpired = (health.subscription?.daysUntilExpiry ?? 0) < 0 || health.subscription?.cancelled;

  const content = (
    <div className={compact ? 'd-flex flex-column gap-2' : 'row g-3'}>
      <div className={compact ? '' : 'col-md-2'}>
        <div className="text-muted small">{t('tenants.detail.pageTexts.health.status')}</div>
        <StatusBadge label={health.tenantStatus.statusName} colour={health.tenantStatus.statusColour} />
      </div>

      <div className={compact ? '' : 'col-md-2'}>
        <div className="text-muted small">{t('tenants.detail.pageTexts.health.subscription')}</div>
        <span className={subscriptionExpired ? 'text-danger fw-medium' : ''}>{subscriptionText}</span>
      </div>

      <div className={compact ? '' : 'col-md-2'}>
        <div className="text-muted small">{t('tenants.detail.pageTexts.health.lastActivity')}</div>
        <span>{health.lastActivityAt ? new Date(health.lastActivityAt).toLocaleString() : t('tenants.detail.pageTexts.health.never')}</span>
      </div>

      <div className={compact ? '' : 'col-md-2'}>
        <div className="text-muted small">{t('tenants.detail.pageTexts.health.configIssues')}</div>
        {health.configIssueCount > 0 ? (
          <Chip
            size="small"
            color="warning"
            label={t('tenants.detail.pageTexts.health.configIssuesCount', { count: health.configIssueCount })}
            onClick={onConfigIssuesClick}
            className={onConfigIssuesClick ? 'cursor-pointer' : ''}
          />
        ) : (
          <Chip size="small" color="success" variant="outlined" label={t('tenants.detail.pageTexts.health.configIssuesNone')} />
        )}
      </div>

      <div className={compact ? '' : 'col-md-2'}>
        <div className="text-muted small">{t('tenants.detail.pageTexts.health.provisioning')}</div>
        {health.provisioning.status === 'failed' ? (
          <div className="d-flex align-items-center gap-2">
            <Chip size="small" color="error" label={t('tenants.detail.pageTexts.health.provisioningFailed')} />
            <Button
              className="btn btn-sm btn-outline-danger"
              text={t('tenants.detail.pageTexts.health.provisioningRetry')}
              loading={retrying}
              onClick={handleRetry}
            />
          </div>
        ) : (
          <Chip size="small" color="success" variant="outlined" label={t('tenants.detail.pageTexts.health.provisioningOk')} />
        )}
      </div>
    </div>
  );

  if (compact) return content;

  return (
    <div className="card mb-4">
      <h6 className="card-header">{t('tenants.detail.pageTexts.health.title')}</h6>
      <hr className="m-0" />
      <div className="card-body">{content}</div>
    </div>
  );
};

export default TenantHealthCard;
